export const PATTERN_COMPONENT_GROUPS = [
  { key: "recent", label: "Recent Form", components: ["PERFECT_5", "FOUR_OF_FIVE"] },
  { key: "momentum", label: "Momentum", components: ["TURNING_UP", "SURGING", "COOLING_OFF"] },
  { key: "bounce", label: "Bounce / Behavior", components: ["BOUNCE", "BOUNCE_SIGNAL", "RUN_THEN_SLIP"] },
  { key: "opportunity", label: "Opportunity", components: ["ROLE_SUPPORTED_BREAKOUT", "PRODUCTION_WITHOUT_OPPORTUNITY_SUPPORT"] },
  { key: "formation", label: "Formation", components: ["BULLISH", "BEARISH", "TRAP_CHOPPY", "MIXED"] },
];

export const PATTERN_COMPONENT_LABELS = {
  PERFECT_5: "PERFECT 5",
  FOUR_OF_FIVE: "4/5 STRONG",
  TURNING_UP: "TURNING UP",
  SURGING: "SURGING",
  COOLING_OFF: "COOLING OFF",
  BOUNCE: "BOUNCE",
  BOUNCE_SIGNAL: "BOUNCE SIGNAL",
  RUN_THEN_SLIP: "RUN THEN SLIP",
  ROLE_SUPPORTED_BREAKOUT: "ROLE-SUPPORTED BREAKOUT",
  PRODUCTION_WITHOUT_OPPORTUNITY_SUPPORT: "PRODUCTION WITHOUT OPPORTUNITY SUPPORT",
  BULLISH: "BULLISH",
  BEARISH: "BEARISH",
  TRAP_CHOPPY: "TRAP / CHOPPY",
  MIXED: "MIXED",
};

const COMPONENT_ALIASES = {
  PRODUCTION_WITHOUT_OPPORTUNITY_SUPPORT: "PRODUCTION_WITHOUT_OPPORTUNITY_SUPPORT",
  ROLE_SUPPORTED_BREAKOUT: "ROLE_SUPPORTED_BREAKOUT",
  "4/5_STRONG": "FOUR_OF_FIVE",
  "4_OF_5_STRONG": "FOUR_OF_FIVE",
  FOUR_OF_FIVE: "FOUR_OF_FIVE",
  PERFECT_5: "PERFECT_5",
  PERFECT5: "PERFECT_5",
  BOUNCE_SIGNAL: "BOUNCE_SIGNAL",
  RUN_THEN_SLIP: "RUN_THEN_SLIP",
  TURNING_UP: "TURNING_UP",
  COOLING_OFF: "COOLING_OFF",
  TRAP_CHOPPY: "TRAP_CHOPPY",
  TRAP: "TRAP_CHOPPY",
  CHOPPY: "TRAP_CHOPPY",
  SURGING: "SURGING",
  BOUNCE: "BOUNCE",
  BULLISH: "BULLISH",
  BEARISH: "BEARISH",
  MIXED: "MIXED",
};

export function rawPatternValue(rowOrValue) {
  if (rowOrValue && typeof rowOrValue === "object") {
    if (String(rowOrValue.Sport ?? "").toUpperCase() === "NBA") {
      const formCode = String(rowOrValue.L5_FormCode ?? "").replace(/[^01]/g, "");
      return formCode ? `L5_SEQUENCE_${formCode}` : "";
    }
    return String(
      rowOrValue.Pattern_L10 ?? rowOrValue["Pattern L10"] ?? rowOrValue.Pattern ??
      rowOrValue.Pattern_Label ?? rowOrValue["Pattern Label"] ?? ""
    ).trim();
  }
  return String(rowOrValue ?? "").trim();
}

export function canonicalExactPattern(rowOrValue) {
  return rawPatternValue(rowOrValue).toUpperCase().replace(/[\s-]+/g, "_").replace(/,+/g, ",").replace(/^,|,$/g, "");
}

export function patternComponents(rowOrValue) {
  const normalized = canonicalExactPattern(rowOrValue);
  const components = normalized ? normalized.split(",")
    .map((token) => token.replace(/[^A-Z0-9/]+/g, "_").replace(/^_+|_+$/g, ""))
    .map((token) => COMPONENT_ALIASES[token])
    .filter(Boolean) : [];
  if (rowOrValue && typeof rowOrValue === "object") {
    const formCode = String(rowOrValue.L5_FormCode ?? "").replace(/[^01]/g, "");
    const hits = [...formCode].filter((value) => value === "1").length;
    if (formCode.length >= 5 && hits === 5) components.push("PERFECT_5");
    else if (formCode.length >= 5 && hits === 4) components.push("FOUR_OF_FIVE");
    const setup = String(rowOrValue.L5_Setup ?? "").toUpperCase();
    if (setup.includes("BOUNCE SIGNAL")) components.push("BOUNCE_SIGNAL");
    if (setup.includes("TURNING UP")) components.push("TURNING_UP");
    if (setup.includes("COOLING OFF")) components.push("COOLING_OFF");
    if (setup.includes("RUN THEN SLIP")) components.push("RUN_THEN_SLIP");
    const formation = String(rowOrValue.Pattern_L10 ?? "").toUpperCase();
    const label = String(rowOrValue.Pattern_Label ?? "").toUpperCase();
    if (formation === "UPTREND") components.push("BULLISH");
    if (formation === "DOWNTREND") components.push("BEARISH");
    if (formation === "NEUTRAL" || String(rowOrValue["Momentum Pattern"] ?? "").toUpperCase() === "MIXED") components.push("MIXED");
    if (label.includes("VOLATILE") || label.includes("BROADENING")) components.push("TRAP_CHOPPY");
  }
  return [...new Set(components)];
}

export function availablePatternComponents(rows) {
  const present = new Set();
  (Array.isArray(rows) ? rows : []).forEach((row) => patternComponents(row).forEach((key) => present.add(key)));
  return PATTERN_COMPONENT_GROUPS.map((group) => ({
    ...group,
    components: group.components.filter((key) => present.has(key)),
  })).filter((group) => group.components.length);
}

export function matchesPatternFilters(row, { components = [], exactPatterns = [], mode = "ALL" } = {}) {
  const rowComponents = new Set(patternComponents(row));
  const selected = Array.isArray(components) ? components : [];
  const componentMatch = selected.length === 0 || (String(mode).toUpperCase() === "ANY"
    ? selected.some((key) => rowComponents.has(key))
    : selected.every((key) => rowComponents.has(key)));
  if (!componentMatch) return false;
  const exact = Array.isArray(exactPatterns) ? exactPatterns : [];
  return exact.length === 0 || exact.includes(canonicalExactPattern(row));
}

export function patternFilterSummary(components, mode = "ALL", exactPatterns = []) {
  const componentLabels = (components || []).map((key) => PATTERN_COMPONENT_LABELS[key] || key);
  const joiner = String(mode).toUpperCase() === "ANY" ? " | " : " + ";
  const parts = componentLabels.length ? [componentLabels.join(joiner)] : [];
  if ((exactPatterns || []).length) parts.push(`Exact: ${exactPatterns.length}`);
  return parts.length ? `Patterns: ${parts.join(" + ")}${componentLabels.length > 1 && String(mode).toUpperCase() === "ANY" ? " (ANY)" : ""}` : "";
}
